import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export const fetchStats = async () => {
  const { data } = await api.get("/stats");
  return data;
};

export const fetchCoils = async (params = {}) => {
  const { data } = await api.get("/coils", { params });
  return data;
};

export const createCoil = async (payload) => {
  const { data } = await api.post("/coils", payload);
  return data;
};

export const deleteCoil = async (id) => {
  const { data } = await api.delete(`/coils/${id}`);
  return data;
};

export const checkCoil = async (coilBarcode) => {
  const { data } = await api.get(`/coils/check/${encodeURIComponent(coilBarcode)}`);
  return data;
};

export const exportCsvUrl = () => `${API}/coils/export/csv`;
export const fgLabelsPdfUrl = (ids) => `${API}/fgs/labels?ids=${encodeURIComponent(ids.join(","))}`;

export const createFGBatch = async (payload) => {
  const { data } = await api.post("/fgs/batch", payload);
  return data;
};
export const listFGs = async (params = {}) => {
  const { data } = await api.get("/fgs", { params });
  return data;
};
export const listTodaysFGs = async () => {
  const { data } = await api.get("/fgs/today");
  return data;
};
export const splitFGPallet = async (fgId, partialWeightKg) => {
  const { data } = await api.post(`/fgs/${encodeURIComponent(fgId)}/split`, { partial_weight_kg: partialWeightKg });
  return data;
};

export const updateCoil = async (id, patch) => {
  const { data } = await api.patch(`/coils/${id}`, patch);
  return data;
};

// SFG SKU-name backfill — Director-only one-shot admin action.
export const sfgSkuBackfillDryRun = async () => {
  const { data } = await api.get("/admin/sfg-sku-backfill/dry-run");
  return data;
};
export const sfgSkuBackfillApply = async () => {
  const { data } = await api.post("/admin/sfg-sku-backfill/apply");
  return data;
};

// Employee dedupe (merge duplicate HR codes) — Director-only.
export const previewEmployeeDedupe = async () => {
  const { data } = await api.get("/hr/admin/dedupe-employees/preview");
  return data;
};
export const applyEmployeeDedupe = async () => {
  const { data } = await api.post("/hr/admin/dedupe-employees/apply");
  return data;
};

export const receiveCoilByBarcode = async (barcode, payload) => {
  const { data } = await api.post(`/coils/by-barcode/${encodeURIComponent(barcode)}/receive`, payload);
  return data;
};

export const extractPackagingList = async (file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/packaging-lists/extract", form, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
  return data;
};

export const commitPackagingList = async (payload) => {
  const { data } = await api.post("/packaging-lists", payload);
  return data;
};

export const listPackagingLists = async (filters = {}) => {
  const params = {};
  if (filters.rm_tag_creation_id) params.rm_tag_creation_id = filters.rm_tag_creation_id;
  if (filters.supplier_name) params.supplier_name = filters.supplier_name;
  if (filters.date_of_dispatch) params.date_of_dispatch = filters.date_of_dispatch;
  const { data } = await api.get("/packaging-lists", { params });
  return data;
};

export const labelsPdfUrl = (pkgId) => `${API}/packaging-lists/${pkgId}/labels.pdf`;
export const extractSfgCounts = async (pkgId, file) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post(`/packaging-lists/${pkgId}/sfg-count/extract`, form, { headers: { "Content-Type": "multipart/form-data" } });
  return data;
};
export const applySfgCounts = async (pkgId, updates) => (await api.post(`/packaging-lists/${pkgId}/sfg-count/apply`, { updates })).data;
export const singleLabelPdfUrl = (coilId) => `${API}/coils/${coilId}/label.pdf`;
export const slitLabelsPdfUrl = (batchId) => `${API}/slitting/${batchId}/labels.pdf`;
// planLabelsPdfUrl / planLabelsTsplUrl live in the print-bridge section below

// ---- Slitting Plans (multi-RM lifecycle) ----
export const createSlittingPlan = async (payload) => {
  const { data } = await api.post("/slitting-plans", payload);
  return data;
};
export const listSlittingPlans = async (params = {}) => {
  const { data } = await api.get("/slitting-plans", { params });
  return data;
};
export const getSlittingPlan = async (planId) => {
  const { data } = await api.get(`/slitting-plans/${encodeURIComponent(planId)}`);
  return data;
};
export const editSlittingPlan = async (planId, payload) => {
  const { data } = await api.patch(`/slitting-plans/${encodeURIComponent(planId)}`, payload);
  return data;
};
export const scanRmForPlan = async (planId, rmBarcode) => {
  const { data } = await api.post(`/slitting-plans/${encodeURIComponent(planId)}/scan-rm`, { rm_barcode: rmBarcode });
  return data;
};
export const unbindRmFromPlan = async (planId, lineId) => {
  const { data } = await api.post(`/slitting-plans/${encodeURIComponent(planId)}/lines/${encodeURIComponent(lineId)}/unbind`);
  return data;
};
export const setPlanLineParts = async (planId, lineId, numParts) =>
  (await api.post(`/slitting-plans/${encodeURIComponent(planId)}/lines/${encodeURIComponent(lineId)}/parts`, { num_parts: numParts })).data;
export const completeSlittingPlan = async (planId, scrapKg, notes = "") => {
  const { data } = await api.post(`/slitting-plans/${encodeURIComponent(planId)}/complete`, { scrap_kg: scrapKg, notes });
  return data;
};
export const reorderSlittingPlans = async (planIds) =>
  (await api.post(`/slitting-plans/reorder`, { plan_ids: planIds })).data;
export const setPlanLinePriority = async (planId, lineId, priorityNo) =>
  (await api.post(`/slitting-plans/${encodeURIComponent(planId)}/lines/${encodeURIComponent(lineId)}/priority`, { priority_no: priorityNo })).data;
export const setPlanPriority = async (planId, priority) => {
  const { data } = await api.post(`/slitting-plans/${encodeURIComponent(planId)}/priority`, { priority });
  return data;
};
export const deleteSlittingPlan = async (planId) => {
  const { data } = await api.delete(`/slitting-plans/${encodeURIComponent(planId)}`);
  return data;
};

export const slitMotherCoil = async (barcode, payload) => {
  const { data } = await api.post(`/coils/by-barcode/${encodeURIComponent(barcode)}/slit`, payload);
  return data;
};

export const reconcileSlitBatch = async (batchId, payload) => {
  const { data } = await api.post(`/slitting/${batchId}/reconcile`, payload);
  return data;
};

export const getCoilTree = async (coilId) => {
  const { data } = await api.get(`/coils/${encodeURIComponent(coilId)}/tree`);
  return data;
};

export const dispatchCoil = async (barcode, payload) => {
  const { data } = await api.post(`/coils/by-barcode/${encodeURIComponent(barcode)}/dispatch`, payload);
  return data;
};

export const listPresses = async () => {
  const { data } = await api.get("/presses");
  return data;
};

export const pressConsumeSFG = async (pressId, payload) => {
  const { data } = await api.post(`/presses/${encodeURIComponent(pressId)}/consume`, payload);
  return data;
};

export const listPressConsumptions = async (pressId, limit = 20) => {
  const { data } = await api.get(`/presses/${encodeURIComponent(pressId)}/consumptions`, { params: { limit } });
  return data;
};

// ---- Masters ----
export const listMasters = async (params = {}) => {
  const { data } = await api.get("/masters", { params });
  return data;
};
export const createMaster = async (payload) => {
  const { data } = await api.post("/masters", payload);
  return data;
};
export const updateMaster = async (id, payload) => {
  const { data } = await api.patch(`/masters/${encodeURIComponent(id)}`, payload);
  return data;
};
export const approveMaster = async (id) => {
  const { data } = await api.post(`/masters/${encodeURIComponent(id)}/approve`);
  return data;
};
export const rejectMaster = async (id) => {
  const { data } = await api.post(`/masters/${encodeURIComponent(id)}/reject`);
  return data;
};
export const deleteMaster = async (id, opts = {}) => {
  const params = {};
  if (opts.relieved_on) params.relieved_on = opts.relieved_on;
  const { data } = await api.delete(`/masters/${encodeURIComponent(id)}`, { params });
  return data;
};
export const seedMastersFromExisting = async () => {
  const { data } = await api.post("/masters/seed-from-existing");
  return data;
};

// ---- Sales Orders ----
export const createSalesOrder = async (payload) => {
  const { data } = await api.post("/sales-orders", payload);
  return data;
};
export const listSalesOrders = async (params = {}) => {
  const { data } = await api.get("/sales-orders", { params });
  return data;
};
export const getSalesOrder = async (id) => {
  const { data } = await api.get(`/sales-orders/${encodeURIComponent(id)}`);
  return data;
};
export const editSalesOrder = async (id, payload) => {
  const { data } = await api.patch(`/sales-orders/${encodeURIComponent(id)}`, payload);
  return data;
};
export const requestShortClose = async (orderId, lineId) => {
  const { data } = await api.post(`/sales-orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/short-close-request`);
  return data;
};
export const approveShortClose = async (orderId, lineId) => {
  const { data } = await api.post(`/sales-orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/short-close-approve`);
  return data;
};
export const deleteSalesOrder = async (id) => {
  const { data } = await api.delete(`/sales-orders/${encodeURIComponent(id)}`);
  return data;
};
export const executeSODispatch = async (orderId, payload) => {
  const { data } = await api.post(`/sales-orders/${encodeURIComponent(orderId)}/execute-dispatch`, payload);
  return data;
};
export const extractEInvoice = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  // Let the browser set multipart/form-data with the correct boundary — the
  // axios instance's default JSON Content-Type would otherwise short-circuit
  // FastAPI's multipart parser and produce a 422 with a Pydantic error array.
  const { data } = await api.post(`/dispatch/extract-einvoice`, fd, {
    headers: { "Content-Type": undefined },
  });
  return data;
};
export const extractKantaParchi = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post(`/dispatch/extract-kanta-parchi`, fd, {
    headers: { "Content-Type": undefined },
  });
  return data;
};
export const listSOScans = async (orderId) => {
  const { data } = await api.get(`/sales-orders/${encodeURIComponent(orderId)}/scans`);
  return data;
};
export const addSOScan = async (orderId, barcode) => {
  const { data } = await api.post(`/sales-orders/${encodeURIComponent(orderId)}/scan`, { barcode });
  return data;
};
export const removeSOScan = async (orderId, coilId) => {
  const { data } = await api.delete(`/sales-orders/${encodeURIComponent(orderId)}/scan/${encodeURIComponent(coilId)}`);
  return data;
};
export const approveDispatchMismatch = async (dispatchId) => {
  const { data } = await api.post(`/dispatches/${encodeURIComponent(dispatchId)}/approve`);
  return data;
};
export const rejectDispatchMismatch = async (dispatchId, action_required) => {
  const { data } = await api.post(
    `/dispatches/${encodeURIComponent(dispatchId)}/reject`,
    { action_required },
  );
  return data;
};
export const getDispatchTolerance = async () => {
  const { data } = await api.get(`/settings/dispatch-tolerance`);
  return data;
};
export const setDispatchTolerance = async (three_way_kg) => {
  const { data } = await api.put(`/settings/dispatch-tolerance`, { three_way_kg });
  return data;
};
export const listDispatchHistory = async (params = {}) => {
  const { data } = await api.get(`/dispatch/history`, { params });
  return data;
};
export const listDispatchHistoryCustomers = async () => {
  const { data } = await api.get(`/dispatch/history/customers`);
  return data;
};
export const dispatchHistoryExportUrl = (fmt, params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return `${API}/dispatch/history/export.${fmt}${qs ? `?${qs}` : ""}`;
};
export const getDispatchDetails = async (dispatchId) => {
  const { data } = await api.get(`/dispatch/history/${encodeURIComponent(dispatchId)}`);
  return data;
};
export const dispatchEInvoiceUrl = (dispatchId) =>
  `${API}/dispatch/history/${encodeURIComponent(dispatchId)}/einvoice`;
export const dispatchKantaParchiUrl = (dispatchId) =>
  `${API}/dispatch/history/${encodeURIComponent(dispatchId)}/kanta-parchi`;
export const getDispatchStock = async (skuName, kind) => {
  const { data } = await api.get(`/dispatch/stock`, { params: { sku_name: skuName, kind } });
  return data;
};
export const scheduleSOLine = async (orderId, lineId, scheduledDate) => {
  const { data } = await api.patch(`/sales-orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(lineId)}/schedule`, { scheduled_date: scheduledDate || "" });
  return data;
};
export const listSODispatches = async (orderId) => {
  const { data } = await api.get(`/sales-orders/${encodeURIComponent(orderId)}/dispatches`);
  return data;
};
export const soGatePassPdfUrl = (orderId) =>
  `${API}/sales-orders/${encodeURIComponent(orderId)}/gate-pass.pdf`;
export const dispatchGatePassPdfUrl = (dispatchId) =>
  `${API}/dispatches/${encodeURIComponent(dispatchId)}/gate-pass.pdf`;
// Fetch a PDF with session auth and show it in `win` (a tab pre-opened inside the click handler, so popups aren't blocked).
export const openPdfInWindow = async (url, win) => {
  try {
    const { data } = await api.get(url.replace(API, ""), { responseType: "blob" });
    const blobUrl = URL.createObjectURL(data);
    if (win && !win.closed) win.location.href = blobUrl; else window.open(blobUrl, "_blank");
  } catch (e) {
    if (win && !win.closed) win.close();
    let msg = "Could not open the Gate Pass";
    try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
    throw new Error(msg);
  }
};
export const soDcrPdfUrl = (orderId) =>
  `${API}/sales-orders/${encodeURIComponent(orderId)}/dcr.pdf`;

// -------- Production Planning --------
export const getProductionPlan = async (weekStart) => {
  const { data } = await api.get(`/production-plan`, { params: { week_start: weekStart } });
  return data;
};
export const suggestProductionPlan = async (weekStart) => {
  const { data } = await api.get(`/production-plan/suggest`, { params: { week_start: weekStart } });
  return data;
};
export const addPlanAllocation = async (soId, lineId, payload) => {
  const { data } = await api.post(`/sales-orders/${encodeURIComponent(soId)}/lines/${encodeURIComponent(lineId)}/plan`, payload);
  return data;
};
export const deletePlanAllocation = async (soId, lineId, allocId) => {
  const { data } = await api.delete(`/sales-orders/${encodeURIComponent(soId)}/lines/${encodeURIComponent(lineId)}/plan/${encodeURIComponent(allocId)}`);
  return data;
};
export const lockSkuToSlot = async (payload) => {
  const { data } = await api.post(`/production-plan/slots`, payload);
  return data;
};
export const unlockSlot = async (slotId) => {
  const { data } = await api.delete(`/production-plan/slots/${encodeURIComponent(slotId)}`);
  return data;
};

// -------- Performance Management --------
export const listEmployees = async () => {
  const { data } = await api.get("/masters", { params: { master_type: "employee" } });
  return data;
};
export const createPerformanceEntry = async (formData) => {
  const { data } = await api.post("/performance/entries", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
};
export const listPerformanceEntries = async (employeeId, fiscalYear) => {
  const params = { employee_id: employeeId };
  if (fiscalYear) params.fiscal_year = fiscalYear;
  const { data } = await api.get("/performance/entries", { params });
  return data;
};
export const deletePerformanceEntry = async (entryId) => {
  const { data } = await api.delete(`/performance/entries/${encodeURIComponent(entryId)}`);
  return data;
};
export const generatePerformanceSummary = async (employeeId, fiscalYear) => {
  const { data } = await api.post("/performance/summary", { employee_id: employeeId, fiscal_year: fiscalYear }, { timeout: 120000 });
  return data;
};
export const performanceAttachmentUrl = (entryId, attachmentId) =>
  `${API}/performance/entries/${encodeURIComponent(entryId)}/attachment/${encodeURIComponent(attachmentId)}`;

// -------- Direct thermal print (DC421D via local bridge on http://127.0.0.1:9911) --------
export const BRIDGE_URL = "http://127.0.0.1:9911";
export const bridgeDownloadUrl = () => `${API}/print-bridge/download`;
export const packagingLabelsTsplUrl = (pkgId) => `${API}/packaging-lists/${encodeURIComponent(pkgId)}/labels.tspl`;
export const coilLabelTsplUrl = (coilId) => `${API}/coils/${encodeURIComponent(coilId)}/label.tspl`;
export const planLabelsPdfUrl = (planId, rmCoilId = "") =>
  `${API}/slitting-plans/${encodeURIComponent(planId)}/labels.pdf${rmCoilId ? `?rm=${encodeURIComponent(rmCoilId)}` : ""}`;
export const planLabelsTsplUrl = (planId, rmCoilId = "") =>
  `${API}/slitting-plans/${encodeURIComponent(planId)}/labels.tspl${rmCoilId ? `?rm=${encodeURIComponent(rmCoilId)}` : ""}`;
export const planJobCardPdfUrl = (planId) => `${API}/slitting-plans/${encodeURIComponent(planId)}/job-card.pdf`;
export const fetchSlittingPriorityTaken = async (excludePlanId) =>
  (await axios.get(`${API}/slitting-plans/priority-taken`, { params: excludePlanId ? { exclude_plan_id: excludePlanId } : {} })).data;
export const getInwardInfo = async (id) => (await api.get(`/coils/by-barcode/${encodeURIComponent(id)}/inward-info`)).data;
export const inwardWeighed = async (id, crane_gross_kg, hold = false) =>
  (await api.post(`/coils/by-barcode/${encodeURIComponent(id)}/inward`, { crane_gross_kg, hold })).data;
export const releaseHoldCoil = async (id) => (await api.post(`/coils/by-barcode/${encodeURIComponent(id)}/release-hold`)).data;
export const getWeighTolerance = async () => (await api.get(`/scale/tolerance`)).data;
export const setWeighTolerance = async (body) => (await api.put(`/scale/tolerance`, body)).data;
export const getCraneWeight = async () => (await api.get(`/scale/latest`)).data;
export const getBundleCandidates = async (id) => (await api.get(`/inward/bundle-candidates/${encodeURIComponent(id)}`)).data;
export const inwardBundle = async (coil_ids, crane_gross_kg, hold = false) =>
  (await api.post(`/inward/bundles`, { coil_ids, crane_gross_kg, hold })).data;
export const bundleLabelTsplUrl = (bundleId) => `${API}/inward/bundles/${encodeURIComponent(bundleId)}/label.tspl`;
export const fetchSlittingRmOptions = async () => (await axios.get(`${API}/slitting-plans/rm-options`)).data;
export const fetchExecutiveDashboard = async () => (await axios.get(`${API}/dashboard/executive`)).data;
export const slitLabelsTsplUrl = (batchId) => `${API}/slitting/${encodeURIComponent(batchId)}/labels.tspl`;
export const fgLabelsTsplUrl = (ids) => `${API}/fgs/labels.tspl?ids=${encodeURIComponent(ids.join(","))}`;

export const isBridgeAlive = async () => {
  try {
    const r = await fetch(BRIDGE_URL, { method: "GET", cache: "no-store" });
    if (!r.ok) return false;
    const j = await r.json();
    return Boolean(j?.ok);
  } catch { return false; }
};

export const printTsplViaBridge = async (tsplUrl) => {
  // 1) Fetch TSPL from our backend (authenticated)
  const r = await api.get(tsplUrl.replace(`${API}`, ""), { responseType: "text" });
  const tspl = r.data;
  // 2) POST it to the local bridge (plain HTTP on 127.0.0.1 — allowed from HTTPS by Chrome)
  const b = await fetch(`${BRIDGE_URL}/print`, {
    method: "POST",
    headers: { "Content-Type": "text/plain" },
    body: tspl,
  });
  const txt = await b.text();
  if (!b.ok) throw new Error(txt || "Bridge returned an error");
  return txt;
};

// -------- HR (Human Resources) --------
export const listHolidays = async (fiscalYear) => {
  const params = {};
  if (fiscalYear) params.fiscal_year = fiscalYear;
  const { data } = await api.get("/hr/holidays", { params });
  return data;
};
export const createHoliday = async (payload) => {
  const { data } = await api.post("/hr/holidays", payload);
  return data;
};
export const deleteHoliday = async (id) => {
  const { data } = await api.delete(`/hr/holidays/${encodeURIComponent(id)}`);
  return data;
};

export const getSalaryRules = async () => {
  const { data } = await api.get("/hr/salary-rules");
  return data;
};
export const updateSalaryRules = async (payload) => {
  const { data } = await api.put("/hr/salary-rules", payload);
  return data;
};

export const uploadAttendance = async (formData) => {
  const { data } = await api.post("/hr/attendance/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 180000,
  });
  return data;
};
export const listAttendance = async ({ employee_id, month } = {}) => {
  const params = {};
  if (employee_id) params.employee_id = employee_id;
  if (month) params.month = month;
  const { data } = await api.get("/hr/attendance", { params });
  return data;
};
export const listAttendanceBatches = async () => {
  const { data } = await api.get("/hr/attendance/batches");
  return data;
};
export const exportAttendanceXlsx = async ({ month, employee_id } = {}) => {
  const params = {};
  if (month) params.month = month;
  if (employee_id) params.employee_id = employee_id;
  const res = await api.get("/hr/attendance/export.xlsx", { params, responseType: "blob" });
  return res.data;
};
// ----- Salary advance bulk upload -----
export const uploadSalaryAdvanceXlsx = async ({ month, file }) => {
  const fd = new FormData();
  fd.append("month", month);
  fd.append("file", file);
  const { data } = await api.post("/hr/salary/advances/upload", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};
export const getSalaryAdvanceUpload = async (month) => {
  const { data } = await api.get("/hr/salary/advances/upload", { params: { month } });
  return data;
};
export const clearSalaryAdvanceUpload = async (month) => {
  const { data } = await api.delete("/hr/salary/advances/upload", { params: { month } });
  return data;
};
export const listSalaryAdvanceSummaries = async (month) => {
  const { data } = await api.get("/hr/salary/advances", { params: { month } });
  return data;
};

export const generateSalary = async (payload) => {
  const { data } = await api.post("/hr/salary/generate", payload, { timeout: 300000 });
  return data;
};
export const listSalaryRecords = async ({ employee_id, month } = {}) => {
  const params = {};
  if (employee_id) params.employee_id = employee_id;
  if (month) params.month = month;
  const { data } = await api.get("/hr/salary/records", { params });
  return data;
};

export const listAdvances = async ({ month, employee_id } = {}) => {
  const params = {};
  if (month) params.month = month;
  if (employee_id) params.employee_id = employee_id;
  const { data } = await api.get("/hr/salary/advances", { params });
  return data;
};
export const createAdvance = async (payload) => {
  const { data } = await api.post("/hr/salary/advances", payload);
  return data;
};
export const deleteAdvance = async (advanceId) => {
  const { data } = await api.delete(`/hr/salary/advances/${encodeURIComponent(advanceId)}`);
  return data;
};
export const salaryExportUrl = (month) => `${API}/hr/salary/export.xlsx?month=${encodeURIComponent(month)}`;

